/**
 * useDataBus — React hooks for reading market history data.
 *
 * MIGRATION NOTE: Data was migrated from MarketDataBus to Zustand store.
 * GlobalHistoryFetcher writes to useMarketStore (Zustand), so all read hooks
 * now read from Zustand instead of the dead MarketDataBus.
 *
 * useActiveHistory and useDataBus are the primary hooks used across the app.
 */

import { useState, useEffect, startTransition, useCallback, useRef } from 'react'
import { marketDataBus } from '../services/MarketDataBus'
import { useSettingsStore } from '../stores/settingsStore'
import { useMarketStore } from '../services/store'

// Frozen empty array — prevents referential instability
const EMPTY_ARRAY: any[] = Object.freeze([]) as unknown as any[]

/**
 * Generic DataBus selector hook.
 * @deprecated Use Zustand selectors directly where possible.
 * Retained for backward compatibility with inspector page.
 */
export function useDataBus<T>(selector: (bus: typeof marketDataBus) => T): T {
    const selectorRef = useRef(selector)
    selectorRef.current = selector

    const [data, setData] = useState<T>(() => selector(marketDataBus))

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        const readData = () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
            debounceRef.current = setTimeout(() => {
                const newData = selectorRef.current(marketDataBus)
                startTransition(() => {
                    setData(newData)
                })
            }, 150)
        }

        const unsubscribe = marketDataBus.subscribe(readData)
        const initialData = selectorRef.current(marketDataBus)
        setData(initialData)

        return () => {
            unsubscribe()
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [])

    return data
}

// ─── Convenience Hooks — NOW READ FROM ZUSTAND ──────────────────────────

/** Get intraday history for a specific symbol — reads from Zustand */
export function useIntradayHistory(symbol: string): any[] {
    return useMarketStore(s => s.intradayHistory?.[symbol] || EMPTY_ARRAY)
}

/** Get historical history for a specific symbol — reads from Zustand */
export function useHistoricalHistoryBus(symbol: string) {
    return useMarketStore(s => s.historicalHistory?.[symbol] || EMPTY_ARRAY)
}

/** Get range history for a specific symbol — reads from Zustand */
export function useRangeHistoryBus(symbol: string) {
    return useMarketStore(s => s.rangeHistory?.[symbol] || EMPTY_ARRAY)
}

/** Get CoG history for a specific symbol — reads from Zustand */
export function useCogHistoryBus(symbol: string) {
    return useMarketStore(s => s.cogHistory?.[symbol] || EMPTY_ARRAY)
}

/** Get Market Watch history for a specific symbol — reads from Zustand */
export function useMarketWatchHistoryBus(symbol: string) {
    return useMarketStore(s => s.marketWatchHistory?.[symbol])
}

/** Get the global DataBus version — reads from Zustand lastUpdated */
export function useDataBusVersion(): number {
    return useMarketStore(s => s.lastUpdated)
}

/**
 * Get the currently active history based on user's inspection settings.
 * NOW READS FROM ZUSTAND (where GlobalHistoryFetcher actually writes data).
 * Previously read from MarketDataBus which was never written to → histLen=0.
 */
export function useActiveHistory(symbol: string): any[] {
    const inspectionSource = useSettingsStore(s => s.inspectionSource)
    
    return useMarketStore(
        useCallback((state) => {
            if (inspectionSource === 'HistoricalSnapshot') {
                return state.historicalHistory?.[symbol] || EMPTY_ARRAY
            } else if (inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD') {
                return state.rangeHistory?.[symbol] || EMPTY_ARRAY
            } else {
                return state.intradayHistory?.[symbol] || EMPTY_ARRAY
            }
        }, [symbol, inspectionSource]),
    )
}
